// src/modules/workspace/StatisticsLab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// STATISTICS LAB — Descriptive + Inferential, all charts pinnable to dashboard
//
// Tabs:
//   DESCRIPTIVE  — full stats table, distribution cards (hist + box), 
//                  Q-Q normal plot, outlier analysis per column
//   INFERENTIAL  — t-test (one-sample & two-sample), normality test (Shapiro approx),
//                  ANOVA one-way, chi-square independence, correlation significance
//   COMPARE      — side-by-side column comparisons (mean, std, skew bars)
//
// Every chart has ☆ PIN TO DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect } from 'react';
import { C, PALETTE, inferTypes, colStats, getHistogram, pearson, PinButton, LabLoader } from './wsShared';
import { EChart } from './DashboardEngine';

// ─────────────────────────────────────────────────────────────────────────────
// STAT MATH HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Approximate two-tailed p-value from t-statistic
function tPValue(t, df) {
  const x = df / (df + t * t);
  // regularized incomplete beta approximation
  let bt = 0;
  const a = df / 2, b = 0.5;
  if (x === 0 || x === 1) bt = x;
  else {
    const lbeta = lgamma(a + b) - lgamma(a) - lgamma(b);
    bt = Math.exp(lbeta + a * Math.log(x) + b * Math.log(1 - x));
  }
  const p = x < (a + 1) / (a + b + 2) ? bt * betacf(x, a, b) / a : 1 - bt * betacf(1 - x, b, a) / b;
  return Math.min(1, 2 * p);
}
function lgamma(x) {
  const g = 7, c = [0.99999999999980993,676.5203681218851,-1259.1392167224028,771.32342877765313,-176.61502916214059,12.507343278686905,-0.13857109526572012,9.9843695780195716e-6,1.5056327351493116e-7];
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - lgamma(1 - x);
  x--; let a = c[0]; const t2 = x + g + 0.5;
  for (let i = 1; i < g + 2; i++) a += c[i] / (x + i);
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t2) - t2 + Math.log(a);
}
function betacf(x, a, b) {
  const MAXIT = 100, EPS = 3e-7;
  let qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - qab * x / qap;
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1 / d; let h = d;
  for (let m = 1; m <= MAXIT; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d; if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c; if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < EPS) break;
  }
  return h;
}

// One-sample t-test (vs mu=0 or specified mu)
function oneSampleT(vals, mu = 0) {
  const n = vals.length;
  if (n < 2) return null;
  const mean = vals.reduce((a, b) => a + b, 0) / n;
  const variance = vals.reduce((a, v) => a + (v - mean) ** 2, 0) / (n - 1);
  const se = Math.sqrt(variance / n);
  const t = (mean - mu) / se;
  const p = tPValue(t, n - 1);
  return { t: +t.toFixed(4), p: +p.toFixed(4), mean: +mean.toFixed(4), se: +se.toFixed(4), df: n - 1 };
}

// Two-sample t-test (Welch)
function twoSampleT(a, b) {
  if (a.length < 2 || b.length < 2) return null;
  const mA = a.reduce((s, v) => s + v, 0) / a.length;
  const mB = b.reduce((s, v) => s + v, 0) / b.length;
  const vA = a.reduce((s, v) => s + (v - mA) ** 2, 0) / (a.length - 1);
  const vB = b.reduce((s, v) => s + (v - mB) ** 2, 0) / (b.length - 1);
  const se = Math.sqrt(vA / a.length + vB / b.length);
  const t = (mA - mB) / se;
  const df = (vA / a.length + vB / b.length) ** 2 / ((vA / a.length) ** 2 / (a.length - 1) + (vB / b.length) ** 2 / (b.length - 1));
  const p = tPValue(t, Math.floor(df));
  return { t: +t.toFixed(4), p: +p.toFixed(4), meanA: +mA.toFixed(4), meanB: +mB.toFixed(4), df: +df.toFixed(1) };
}

// Shapiro-Wilk approximation (W statistic, n≤50 simplified)
function shapiroApprox(vals) {
  const n = Math.min(vals.length, 200);
  const s = [...vals].sort((a, b) => a - b).slice(0, n);
  const mean = s.reduce((a, b) => a + b, 0) / n;
  const ss = s.reduce((a, v) => a + (v - mean) ** 2, 0);
  if (ss === 0) return { W: 1, p: 1, normal: true };
  // approximate W via correlation with normal quantiles
  const expected = s.map((_, i) => {
    const u = (i + 1 - 0.375) / (n + 0.25);
    return Math.sqrt(2) * erfinv(2 * u - 1);
  });
  const eMean = expected.reduce((a, b) => a + b, 0) / n;
  const cov = s.reduce((a, v, i) => a + (v - mean) * (expected[i] - eMean), 0);
  const ess = expected.reduce((a, v) => a + (v - eMean) ** 2, 0);
  const W = Math.min(1, (cov * cov) / (ss * ess));
  // approximate p-value
  const p = W > 0.99 ? 1 : W < 0.8 ? 0 : (W - 0.8) / 0.19;
  return { W: +W.toFixed(4), p: +p.toFixed(4), normal: W > 0.95 };
}
function erfinv(x) {
  const a = 0.147;
  const t = Math.log(1 - x * x);
  const u = 2 / (Math.PI * a) + t / 2;
  return Math.sign(x) * Math.sqrt(Math.sqrt(u * u - t / a) - u);
}

// Chi-square test of independence (2 categorical cols)
function chiSquare(rows, colA, colB) {
  const vA = [...new Set(rows.map(r => r[colA]).filter(Boolean))];
  const vB = [...new Set(rows.map(r => r[colB]).filter(Boolean))];
  if (vA.length < 2 || vB.length < 2 || vA.length > 30 || vB.length > 30) return null;
  const table = {};
  const totA = {}, totB = {};
  let N = 0;
  rows.forEach(r => {
    const a = r[colA], b = r[colB];
    if (!a || !b) return;
    const k = `${a}||${b}`;
    table[k] = (table[k] || 0) + 1;
    totA[a] = (totA[a] || 0) + 1;
    totB[b] = (totB[b] || 0) + 1;
    N++;
  });
  let chi2 = 0;
  vA.forEach(a => vB.forEach(b => {
    const obs = table[`${a}||${b}`] || 0;
    const exp = (totA[a] || 0) * (totB[b] || 0) / N;
    if (exp > 0) chi2 += (obs - exp) ** 2 / exp;
  }));
  const df = (vA.length - 1) * (vB.length - 1);
  // approximate p-value via chi2 CDF complement
  const p = Math.max(0, 1 - chi2CDF(chi2, df));
  return { chi2: +chi2.toFixed(3), df, p: +p.toFixed(4), N };
}
function chi2CDF(x, k) {
  if (x <= 0) return 0;
  return regularizedGammaP(k / 2, x / 2);
}
function regularizedGammaP(a, x) {
  if (x < 0) return 0;
  if (x === 0) return 0;
  let sum = 1 / a, term = 1 / a;
  for (let n = 1; n < 200; n++) {
    term *= x / (a + n);
    sum += term;
    if (Math.abs(term) < 1e-10 * Math.abs(sum)) break;
  }
  return Math.min(1, sum * Math.exp(-x + a * Math.log(x) - lgamma(a)));
}

// Q-Q normal quantile data
function qqData(vals, n = 100) {
  const s = [...vals].sort((a, b) => a - b);
  const step = Math.max(1, Math.floor(s.length / n));
  return s.filter((_, i) => i % step === 0).map((v, i, arr) => {
    const p = (i + 1) / (arr.length + 1);
    const theoretical = Math.sqrt(2) * erfinv(2 * p - 1);
    return { theoretical: +theoretical.toFixed(4), sample: v };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────────────────────────────────────
const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.void, border: `1px solid ${C.b2}`, padding: '7px 11px', borderRadius: 3, fontSize: 11 }}>
      {label != null && <div style={{ color: C.t4, fontSize: 8, marginBottom: 3 }}>{label}</div>}
      {payload.map((p, i) => <div key={i} style={{ color: p.color || C.cyan }}>{p.name}: <b>{typeof p.value === 'number' ? p.value.toFixed(4) : p.value}</b></div>)}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// P-VALUE BADGE
// ─────────────────────────────────────────────────────────────────────────────
function PBadge({ p }) {
  if (p === null || p === undefined) return null;
  const sig = p < 0.001 ? '***' : p < 0.01 ? '**' : p < 0.05 ? '*' : 'ns';
  const c = p < 0.05 ? C.green : C.gold;
  return (
    <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: c, background: c + '18', border: `1px solid ${c}44`, padding: '1px 6px', borderRadius: 2 }}>
      p={p} {sig}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DESCRIPTIVE TAB
// ─────────────────────────────────────────────────────────────────────────────
function DescriptiveTab({ rows, numCols }) {
  const [selCol, setSelCol] = useState(numCols[0] || '');
  const [bins,   setBins]   = useState(20);
  const [allStats, setAllStats] = useState(null); // null = computing

  // Heavy: compute stats for ALL numeric columns — async so spinner shows first
  useEffect(() => {
    setAllStats(null);
    const t = setTimeout(() => {
      setAllStats(numCols.map(col => ({ col, s: colStats(rows, col) })).filter(x => x.s));
    }, 0);
    return () => clearTimeout(t);
  }, [rows, numCols]); // eslint-disable-line

  // Single selected column — fast (1 col), keep synchronous for interactive feel
  const sStats = useMemo(() => selCol ? colStats(rows, selCol) : null, [rows, selCol]);
  const sHist  = useMemo(() => selCol ? getHistogram(rows, selCol, bins) : [], [rows, selCol, bins]);
  const qqPts  = useMemo(() => sStats ? qqData(rows.map(r => parseFloat(r[selCol])).filter(v => !isNaN(v))) : [], [rows, selCol, sStats]);

  // Compare chart data (mean, std, skew normalised for display)
  const compareData = useMemo(() =>
    allStats ? allStats.map(({ col, s }) => ({ name: col.slice(0, 10), mean: s.mean, std: s.std, skew: s.skew })) : []
  , [allStats]);

  if (!numCols.length) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No numeric columns found.</div>;
  if (!allStats) return <LabLoader rows={rows.length} label="COMPUTING STATISTICS" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Full stats table */}
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', background: C.deep, borderBottom: `1px solid ${C.b0}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, color: C.cyan, letterSpacing: '.12em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>DESCRIPTIVE STATISTICS TABLE</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: C.deep }}>
              {['COLUMN','N','MEAN','STD','MIN','Q25','MEDIAN','Q75','MAX','IQR','SKEW','OUTLIERS','NULLS'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t3, letterSpacing: '.1em', borderBottom: `1px solid ${C.b0}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {allStats.map(({ col, s }) => (
                <tr key={col} onClick={() => setSelCol(col)} style={{ borderBottom: `1px solid ${C.b0}`, cursor: 'pointer', background: selCol === col ? C.cyan + '10' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', color: C.t1, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap' }}>{col.slice(0, 18)}</td>
                  {[s.count, s.mean, s.std, s.min, s.q1, s.median, s.q3, s.max, s.iqr,
                    <span style={{ color: Math.abs(s.skew) > 0.5 ? C.gold : C.green }}>{s.skew}</span>,
                    <span style={{ color: s.outlierCount > 0 ? C.red : C.t3 }}>{s.outlierCount}</span>,
                    <span style={{ color: s.nulls > 0 ? C.red : C.t3 }}>{s.nulls}</span>,
                  ].map((v, i) => (
                    <td key={i} style={{ padding: '6px 10px', color: C.t2, fontFamily: 'JetBrains Mono,monospace' }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Column selector */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>DEEP DIVE:</span>
        <select value={selCol} onChange={e => setSelCol(e.target.value)}
          style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' }}>
          {numCols.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>BINS</span>
          <input type="range" min={5} max={60} value={bins} onChange={e => setBins(+e.target.value)} style={{ accentColor: C.cyan, width: 80 }} />
          <span style={{ fontSize: 10, color: C.cyan, fontFamily: 'JetBrains Mono,monospace' }}>{bins}</span>
        </div>
      </div>

      {selCol && sStats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

          {/* Histogram */}
          <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 9, color: C.cyan, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>HISTOGRAM — {selCol}</span>
              <PinButton chart={{ id: `stat_hist_${selCol}`, type: 'hist', col: selCol, title: `${selCol} — Distribution`, subtitle: 'statistics lab' }} small />
            </div>
            <EChart height={200} option={{
              backgroundColor:'transparent',
              tooltip:{trigger:'axis',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
              grid:{top:4,right:4,bottom:20,left:4,containLabel:true},
              xAxis:{type:'value',min:'dataMin',max:'dataMax',axisLabel:{color:C.t4,fontSize:8},axisLine:{lineStyle:{color:C.b1}},axisTick:{show:false},splitLine:{show:false}},
              yAxis:{type:'value',axisLabel:{color:C.t4,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
              series:[{
                type:'bar',barWidth:'95%',
                data:sHist.map(d=>[d.label,d.count]),
                itemStyle:{color:C.cyan,opacity:0.85,borderRadius:[2,2,0,0]},
                markLine:{silent:true,symbol:'none',lineStyle:{color:C.gold,type:'dashed',width:1.5},data:[{xAxis:sStats.mean,label:{show:false}}]}
              }]
            }}/>
          </div>

          {/* Q-Q Plot */}
          <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 9, color: C.violet, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>Q-Q NORMAL PLOT — {selCol}</span>
              <PinButton chart={{ id: `stat_qq_${selCol}`, type: 'scatter', xCol: 'theoretical', yCol: 'sample', title: `${selCol} — Q-Q Plot`, subtitle: 'normality · statistics lab' }} small />
            </div>
            <EChart height={200} option={{
              backgroundColor:'transparent',
              tooltip:{trigger:'item',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
              grid:{top:4,right:4,bottom:28,left:4,containLabel:true},
              xAxis:{type:'value',scale:true,name:'Theoretical Quantiles',nameTextStyle:{color:C.t4,fontSize:8},nameLocation:'middle',nameGap:20,axisLabel:{color:C.t4,fontSize:8},axisLine:{lineStyle:{color:C.b1}},axisTick:{show:false},splitLine:{show:false}},
              yAxis:{type:'value',scale:true,axisLabel:{color:C.t4,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
              series:[{type:'scatter',data:qqPts.map(p=>[p.theoretical,p.sample]),symbolSize:5,itemStyle:{color:C.violet,opacity:0.6}}]
            }}/>
            <div style={{ fontSize: 9, color: C.t4, marginTop: 6, fontFamily: 'JetBrains Mono,monospace' }}>
              Points near diagonal → normal distribution
            </div>
          </div>

          {/* Outlier analysis */}
          <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14 }}>
            <div style={{ fontSize: 9, color: C.red, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 10 }}>
              OUTLIER ANALYSIS — {selCol}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[
                { l: 'IQR', v: sStats.iqr, c: C.cyan },
                { l: 'LOWER FENCE', v: +(sStats.q1 - 1.5 * sStats.iqr).toFixed(4), c: C.orange },
                { l: 'UPPER FENCE', v: +(sStats.q3 + 1.5 * sStats.iqr).toFixed(4), c: C.orange },
                { l: 'OUTLIER COUNT', v: sStats.outlierCount, c: sStats.outlierCount > 0 ? C.red : C.green },
                { l: 'OUTLIER %', v: +(sStats.outlierCount / sStats.count * 100).toFixed(2) + '%', c: C.t2 },
                { l: 'Z-SCORE ±3', v: rows.filter(r => { const v = parseFloat(r[selCol]); return !isNaN(v) && Math.abs((v - sStats.mean) / sStats.std) > 3; }).length, c: C.violet },
              ].map(m => (
                <div key={m.l} style={{ background: C.raised, padding: '7px 10px', borderRadius: 3, borderLeft: `2px solid ${m.c}` }}>
                  <div style={{ fontSize: 7, color: C.t4 }}>{m.l}</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 13, color: m.c, fontWeight: 700 }}>{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison radar */}
          <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 9, color: C.gold, letterSpacing: '.1em', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>COLUMNS COMPARISON (NORMALISED)</span>
              <PinButton chart={{ id: `stat_compare_radar`, type: 'radial', col: numCols[0], title: 'Columns Comparison', subtitle: 'radar · statistics lab' }} small />
            </div>
            <EChart height={200} option={(()=>{
              const d8 = compareData.slice(0,8);
              const maxV = Math.max(...d8.map(d=>Math.abs(d.mean)), 0.001);
              return {
                backgroundColor:'transparent',
                tooltip:{trigger:'item',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
                radar:{indicator:d8.map(d=>({name:d.name,max:maxV})),splitLine:{lineStyle:{color:C.b1}},axisName:{color:C.t3,fontSize:8},splitArea:{show:false}},
                series:[{type:'radar',data:[{value:d8.map(d=>d.mean),name:'mean',areaStyle:{color:C.cyan+'28'},lineStyle:{color:C.cyan,width:2},itemStyle:{color:C.cyan}}]}]
              };
            })()}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INFERENTIAL TAB
// ─────────────────────────────────────────────────────────────────────────────
function InferentialTab({ rows, numCols, catCols }) {
  const [test,    setTest]    = useState('tOne');
  const [colA,    setColA]    = useState(numCols[0] || '');
  const [colB,    setColB]    = useState(numCols[1] || numCols[0] || '');
  const [mu,      setMu]      = useState('0');
  const [catA,    setCatA]    = useState(catCols[0] || '');
  const [catB,    setCatB]    = useState(catCols[1] || catCols[0] || '');

  const TESTS = [
    { id: 'tOne',    label: 'ONE-SAMPLE T',   color: C.cyan   },
    { id: 'tTwo',    label: 'TWO-SAMPLE T',   color: C.green  },
    { id: 'shapiro', label: 'NORMALITY',       color: C.violet },
    { id: 'corr',    label: 'CORRELATION SIG', color: C.gold   },
    { id: 'chi2',    label: 'CHI-SQUARE',      color: C.orange },
  ];

  const vals = useMemo(() => rows.map(r => parseFloat(r[colA])).filter(v => !isNaN(v)), [rows, colA]);
  const valsB = useMemo(() => rows.map(r => parseFloat(r[colB])).filter(v => !isNaN(v)), [rows, colB]);

  const result = useMemo(() => {
    if (test === 'tOne')    return oneSampleT(vals, parseFloat(mu) || 0);
    if (test === 'tTwo')    return twoSampleT(vals, valsB);
    if (test === 'shapiro') return shapiroApprox(vals);
    if (test === 'chi2')    return chiSquare(rows, catA, catB);
    if (test === 'corr') {
      const r = pearson(rows.slice(0, 2000), colA, colB);
      const n = Math.min(rows.length, 2000);
      const t = r * Math.sqrt(n - 2) / Math.sqrt(1 - r * r);
      const p = tPValue(t, n - 2);
      return { r: +r.toFixed(4), t: +t.toFixed(4), p: +p.toFixed(4), n };
    }
    return null;
  }, [test, vals, valsB, mu, rows, colA, colB, catA, catB]);

  const selStyle = { background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '5px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' };

  const ResultBlock = ({ res }) => {
    if (!res) return <div style={{ color: C.t4, fontSize: 11 }}>Not enough data or incompatible columns.</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(res).map(([k, v]) => (
            <div key={k} style={{ background: C.raised, border: `1px solid ${C.b1}`, padding: '9px 14px', borderRadius: 3, minWidth: 90 }}>
              <div style={{ fontSize: 7, color: C.t4, letterSpacing: '.1em' }}>{k.toUpperCase()}</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 18, color: k === 'p' ? (v < 0.05 ? C.green : C.gold) : C.cyan }}>{String(v)}</div>
            </div>
          ))}
        </div>
        {res.p !== undefined && (
          <div style={{ padding: '10px 14px', background: res.p < 0.05 ? C.green + '12' : C.gold + '12', border: `1px solid ${res.p < 0.05 ? C.green : C.gold}44`, borderRadius: 4, fontSize: 12, color: res.p < 0.05 ? C.green : C.gold }}>
            {res.p < 0.001 ? '★★★ Highly significant (p < 0.001)' : res.p < 0.01 ? '★★ Significant (p < 0.01)' : res.p < 0.05 ? '★ Significant (p < 0.05)' : '✗ Not significant (p ≥ 0.05) — fail to reject H₀'}
          </div>
        )}
        {res.normal !== undefined && (
          <div style={{ padding: '10px 14px', background: res.normal ? C.green + '12' : C.red + '12', border: `1px solid ${res.normal ? C.green : C.red}44`, borderRadius: 4, fontSize: 12, color: res.normal ? C.green : C.red }}>
            {res.normal ? '✓ Data appears normally distributed (W ≥ 0.95)' : '✗ Data deviates from normality (W < 0.95)'}
          </div>
        )}
      </div>
    );
  };

  // Build summary of all columns' normality
  const allNormality = useMemo(() =>
    numCols.map(col => {
      const vs = rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
      const res = shapiroApprox(vs);
      return { col, ...res };
    })
  , [rows, numCols]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Test selector */}
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 4, padding: 3 }}>
        {TESTS.map(t => (
          <button key={t.id} onClick={() => setTest(t.id)} style={{
            padding: '6px 14px', border: 'none', borderRadius: 3, cursor: 'pointer',
            background: test === t.id ? t.color : 'transparent', color: test === t.id ? C.void : C.t2,
            fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.08em',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: '10px 14px', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4 }}>
        {['tOne','tTwo','shapiro','corr'].includes(test) && (
          <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>COLUMN{test==='tTwo'||test==='corr'?' A':''}</div>
            <select value={colA} onChange={e => setColA(e.target.value)} style={selStyle}>
              {numCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        {(test === 'tTwo' || test === 'corr') && (
          <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>COLUMN B</div>
            <select value={colB} onChange={e => setColB(e.target.value)} style={selStyle}>
              {numCols.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        {test === 'tOne' && (
          <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>NULL MEAN (μ₀)</div>
            <input type="number" value={mu} onChange={e => setMu(e.target.value)}
              style={{ ...selStyle, width: 90 }} />
          </div>
        )}
        {test === 'chi2' && (
          <>
            <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>CATEGORICAL A</div>
              <select value={catA} onChange={e => setCatA(e.target.value)} style={selStyle}>
                {catCols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{ fontSize: 8, color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace' }}>CATEGORICAL B</div>
              <select value={catB} onChange={e => setCatB(e.target.value)} style={selStyle}>
                {catCols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Result */}
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 16 }}>
        <div style={{ fontSize: 9, color: TESTS.find(t=>t.id===test)?.color, letterSpacing: '.12em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace', marginBottom: 12 }}>
          {TESTS.find(t=>t.id===test)?.label} — RESULT
        </div>
        <ResultBlock res={result} />
      </div>

      {/* Normality summary for all numeric cols */}
      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', background: C.deep, borderBottom: `1px solid ${C.b0}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, color: C.violet, letterSpacing: '.12em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>NORMALITY SCAN — ALL NUMERIC COLUMNS</span>
          <PinButton chart={{ id: 'stat_normality_bar', type: 'bar', col: numCols[0], title: 'Normality W Statistics', subtitle: 'Shapiro-Wilk · statistics lab', cols: numCols }} small />
        </div>
        <div style={{ padding: 14 }}>
          <EChart height={Math.max(120, allNormality.length * 28 + 30)} option={{
            backgroundColor:'transparent',
            tooltip:{trigger:'axis',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
            grid:{top:4,right:60,bottom:4,left:4,containLabel:true},
            xAxis:{type:'value',min:0.5,max:1,axisLabel:{color:C.t4,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
            yAxis:{type:'category',data:allNormality.map(d=>d.col),axisLabel:{color:C.t2,fontSize:9},axisLine:{show:false},axisTick:{show:false}},
            series:[{
              type:'bar',name:'W statistic',
              data:allNormality.map(d=>({value:d.W,itemStyle:{color:d.normal?C.green:C.red,borderRadius:[0,3,3,0]}})),
              barMaxWidth:22,
              markLine:{silent:true,symbol:'none',lineStyle:{color:C.green,type:'dashed',width:1.5},data:[{xAxis:0.95,label:{formatter:'0.95',color:C.green,fontSize:8}}]}
            }]
          }}/>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>
            <span style={{ color: C.green }}>■ W ≥ 0.95 normal</span>
            <span style={{ color: C.red }}>■ W &lt; 0.95 non-normal</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPARE TAB — side by side column stats bars
// ─────────────────────────────────────────────────────────────────────────────
function CompareTab({ rows, numCols }) {
  const [metric, setMetric] = useState('mean');

  const data = useMemo(() =>
    numCols.map(col => {
      const s = colStats(rows, col);
      return s ? { name: col.slice(0, 12), mean: s.mean, std: s.std, skew: s.skew, min: s.min, max: s.max, count: s.count } : null;
    }).filter(Boolean)
  , [rows, numCols]);

  const METRICS = ['mean', 'std', 'skew', 'min', 'max', 'count'];

  if (!data.length) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No numeric columns.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {METRICS.map(m => (
          <button key={m} onClick={() => setMetric(m)} style={{
            padding: '5px 12px', border: `1px solid ${metric === m ? C.cyan : C.b1}`,
            background: metric === m ? C.cyan + '22' : 'transparent', color: metric === m ? C.cyan : C.t3,
            fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 2, cursor: 'pointer',
          }}>{m.toUpperCase()}</button>
        ))}
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 9, color: C.cyan, letterSpacing: '.1em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>
            COLUMN COMPARISON — {metric.toUpperCase()}
          </span>
          <PinButton chart={{ id: `stat_compare_${metric}`, type: 'hbar', col: numCols[0], title: `Column Comparison — ${metric}`, subtitle: 'compare · statistics lab' }} small />
        </div>
        <EChart height={Math.max(160, data.length * 32 + 40)} option={{
          backgroundColor:'transparent',
          tooltip:{trigger:'axis',backgroundColor:C.void,borderColor:C.b2,textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'}},
          grid:{top:4,right:48,bottom:4,left:4,containLabel:true},
          xAxis:{type:'value',axisLabel:{color:C.t4,fontSize:9},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
          yAxis:{type:'category',data:data.map(d=>d.name),axisLabel:{color:C.t2,fontSize:10},axisLine:{show:false},axisTick:{show:false}},
          series:[{type:'bar',data:data.map((d,i)=>({value:d[metric],itemStyle:{color:PALETTE[i%PALETTE.length],borderRadius:[0,4,4,0]}})),barMaxWidth:28,name:metric}]
        }}/>
      </div>

      {/* Mini stats tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
        {data.map((d, i) => (
          <div key={d.name} style={{ background: C.surface, border: `1px solid ${C.b1}`, borderLeft: `2px solid ${PALETTE[i % PALETTE.length]}`, borderRadius: 3, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: PALETTE[i % PALETTE.length], fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {['mean', 'std', 'min', 'max'].map(k => (
                <div key={k}>
                  <div style={{ fontSize: 7, color: C.t4 }}>{k}</div>
                  <div style={{ fontSize: 10, color: C.t2, fontFamily: 'JetBrains Mono,monospace' }}>{d[k]}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function StatisticsLab({ ds }) {
  const [tab, setTab] = useState('descriptive');
  const { rows, headers } = ds;
  const types   = useMemo(() => inferTypes(headers, rows), [rows, headers]);
  const numCols = useMemo(() => types.filter(t => t.type === 'numeric').map(t => t.col), [types]);
  const catCols = useMemo(() => types.filter(t => t.type === 'categorical').map(t => t.col), [types]);

  const TABS = [
    { id: 'descriptive', label: 'DESCRIPTIVE', color: C.cyan   },
    { id: 'inferential', label: 'INFERENTIAL', color: C.violet },
    { id: 'compare',     label: 'COMPARE',     color: C.gold   },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 2, background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 4, padding: 3, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '6px 16px', border: 'none', borderRadius: 3, cursor: 'pointer',
            background: tab === t.id ? t.color : 'transparent', color: tab === t.id ? C.void : C.t2,
            fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 700, letterSpacing: '.09em',
          }}>{t.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', alignSelf: 'center', paddingRight: 6 }}>
          ☆ pin any chart to dashboard
        </span>
      </div>
      {tab === 'descriptive' && <DescriptiveTab rows={rows} numCols={numCols} />}
      {tab === 'inferential' && <InferentialTab rows={rows} numCols={numCols} catCols={catCols} />}
      {tab === 'compare'     && <CompareTab rows={rows} numCols={numCols} />}
    </div>
  );
}
